<script lang="ts">
  import { ChevronDown, Menu, Phone, ShoppingCart, User, X } from '@lucide/svelte'
  import { getContext } from 'svelte'
  import { page } from '@inertiajs/svelte'
  import { SITE_CONTACT } from '~/lib/site-contact'
  import { siteContainerClass } from '~/app/site-theme'
  import SiteLogoMark from '~/components/site/SiteLogoMark.svelte'
  import { SITE_UI_KEY, type SiteUiState } from '~/lib/site-ui-context.svelte'
  import { siteCartStore } from '~/lib/site-cart-store.svelte'
  import { resolveSiteCart } from '~/lib/site-cart-view'
  import { siteCsaNavLink, siteInfoNavLinks } from '~/lib/site-info-nav'

  let mobileOpen = $state(false)
  let catalogOpen = $state(false)

  const navCategories = $derived(
    ($page.props.navCategories as { name: string; slug: string | null }[]) ?? []
  )

  const siteCustomer = $derived(
    $page.props.siteCustomer as { fullName: string; approvalStatus: string } | null
  )

  const siteUi = getContext<SiteUiState>(SITE_UI_KEY)

  const canUseCart = $derived(
    siteCustomer?.approvalStatus === 'approved'
  )

  const displayCart = $derived(
    resolveSiteCart(siteCartStore.snapshot, $page.props as Record<string, unknown>)
  )

  function openCart() {
    if (!canUseCart) return
    siteUi.openCartDrawer()
  }

  function closeMobile() {
    mobileOpen = false
    catalogOpen = false
  }

  function closeCatalogOnFocusOut(event: FocusEvent) {
    const nextTarget = event.relatedTarget

    if (nextTarget instanceof Node && event.currentTarget instanceof HTMLElement) {
      catalogOpen = event.currentTarget.contains(nextTarget)
      return
    }

    catalogOpen = false
  }

  let headerHeight = $state(152)

  $effect(() => {
    document.documentElement.style.setProperty('--site-header-height', `${headerHeight}px`)
  })

  function handleCatalogKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape') {
      return
    }

    catalogOpen = false

    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.querySelector<HTMLButtonElement>('button')?.focus()
    }
  }
</script>

<!-- Top info bar -->
<div class="hidden border-b border-[rgba(122,181,71,0.1)] bg-[#060d07] sm:block">
  <div class="{siteContainerClass} flex items-center justify-between py-2 text-xs text-[#dfe8d8]">
    <a
      href={SITE_CONTACT.phoneHref}
      class="flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-[#7AB547]"
    >
      <Phone class="size-3.5" />
      {SITE_CONTACT.phone}
    </a>
    <span>{SITE_CONTACT.address}</span>
  </div>
</div>

<!-- Main header -->
<header
  id="site-header"
  bind:offsetHeight={headerHeight}
  class="relative z-30 w-full max-w-full border-b border-[rgba(122,181,71,0.15)] bg-[#09100a]"
>
  <div class="{siteContainerClass} flex min-h-0 items-center justify-between gap-2 py-3 sm:gap-4 sm:py-4 lg:min-h-[120px]">
    <!-- Logo -->
    <a href="/" class="flex shrink-0 items-center">
      <span class="text-[#7AB547]">
        <SiteLogoMark class="size-20 sm:size-24 lg:size-[120px]" />
      </span>
    </a>

    <!-- Desktop nav -->
    <nav class="hidden items-center gap-1 lg:flex" aria-label="Навигация">
      <!-- Каталог с дропдауном -->
      {#if navCategories.length > 0}
        <div
          class="relative -my-2 py-2"
          role="none"
          onmouseenter={() => (catalogOpen = true)}
          onmouseleave={() => (catalogOpen = false)}
          onfocusout={closeCatalogOnFocusOut}
          onkeydown={handleCatalogKeydown}
        >
          <button
            type="button"
            class="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[#7AB547]/10 hover:text-[#7AB547]"
            aria-controls="site-catalog-menu"
            aria-expanded={catalogOpen}
            aria-haspopup="true"
            onfocus={() => (catalogOpen = true)}
            onclick={() => (catalogOpen = !catalogOpen)}
          >
            Каталог
            <ChevronDown class="size-3.5 transition-transform {catalogOpen ? 'rotate-180' : ''}" />
          </button>
          {#if catalogOpen}
            <div
              id="site-catalog-menu"
              class="absolute top-full left-0 min-w-[180px] pt-2"
            >
              <div
                class="rounded-xl border border-[rgba(122,181,71,0.2)] bg-[#111d12] py-1.5 shadow-2xl shadow-black/50"
                role="menu"
                tabindex="-1"
              >
                {#each navCategories as cat (cat.name)}
                  {#if cat.slug}
                    <a
                      href="/catalog#cat-{cat.slug}"
                      class="block px-4 py-2 text-sm text-[#f4f8f1] transition-colors hover:bg-[#7AB547]/10 hover:text-white"
                      role="menuitem"
                    >
                      {cat.name}
                    </a>
                  {/if}
                {/each}
                <div class="my-1 border-t border-[rgba(122,181,71,0.1)]"></div>
                <a
                  href="/catalog"
                  class="block px-4 py-2 text-sm font-medium text-[#7AB547] transition-colors hover:bg-[#7AB547]/10"
                  role="menuitem"
                >
                  Весь каталог →
                </a>
              </div>
            </div>
          {/if}
        </div>
      {:else}
        <a
          href="/catalog"
          class="rounded-lg px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[#7AB547]/10 hover:text-[#7AB547]"
        >
          Каталог
        </a>
      {/if}

      <a
        href={siteCsaNavLink.href}
        class="rounded-lg px-3 py-2 text-sm font-medium text-[#f4f8f1] transition-colors hover:bg-[#7AB547]/10 hover:text-white"
      >
        {siteCsaNavLink.label}
      </a>

      {#each siteInfoNavLinks as link (link.href)}
        <a
          href={link.href}
          class="rounded-lg px-3 py-2 text-sm text-[#f4f8f1] transition-colors hover:bg-[#7AB547]/10 hover:text-white"
        >
          {link.label}
        </a>
      {/each}
    </nav>

    <!-- Right side -->
    <div class="flex items-center gap-2">
      <!-- Cart (desktop) -->
      {#if canUseCart}
        <button
          type="button"
          class="relative hidden items-center gap-2 rounded-lg border border-[rgba(122,181,71,0.3)] px-3 py-2 text-sm text-white transition-colors hover:bg-[#7AB547]/10 sm:flex {displayCart.count > 0
            ? 'bg-[#7AB547]/15'
            : ''}"
          aria-label="Корзина"
          onclick={openCart}
        >
          <ShoppingCart class="size-4 text-[#7AB547]" />
          {#if displayCart.count > 0}
            <span class="font-medium">{displayCart.count}</span>
          {/if}
        </button>
      {/if}

      <!-- Account -->
      {#if siteCustomer}
        <a
          href="/account"
          class="flex items-center gap-1.5 rounded-xl border border-[rgba(122,181,71,0.3)] px-3 py-2 text-sm text-white transition-colors hover:border-[#7AB547]/50 hover:bg-[#7AB547]/10"
        >
          <User class="size-4 text-[#7AB547]" />
          <span class="hidden max-w-28 truncate md:inline text-xs">{siteCustomer.fullName}</span>
        </a>
      {:else}
        <a
          href="/login"
          class="hidden rounded-xl border border-[rgba(122,181,71,0.3)] px-4 py-2 text-sm font-medium text-white transition-all hover:border-[#7AB547] hover:bg-[#7AB547]/10 sm:inline-flex"
        >
          Войти
        </a>
        <a
          href="/register"
          class="hidden rounded-xl bg-[#7AB547] px-4 py-2 text-sm font-semibold text-[#09100a] transition-colors hover:bg-[#6ba03e] sm:inline-flex"
        >
          Регистрация
        </a>
      {/if}

      <!-- Mobile menu button -->
      <button
        type="button"
        class="rounded-lg p-2 text-[#f4f8f1] transition-colors hover:bg-[#7AB547]/10 hover:text-white lg:hidden"
        aria-label={mobileOpen ? 'Закрыть меню' : 'Открыть меню'}
        onclick={() => (mobileOpen = !mobileOpen)}
      >
        {#if mobileOpen}
          <X class="size-5" />
        {:else}
          <Menu class="size-5" />
        {/if}
      </button>
    </div>
  </div>

  <!-- Mobile menu -->
  {#if mobileOpen}
    <div class="border-t border-[rgba(122,181,71,0.1)] bg-[#09100a] px-4 pb-4 pt-3 lg:hidden">
      <!-- Phone (mobile top) -->
      <a
        href={SITE_CONTACT.phoneHref}
        class="mb-3 flex items-center gap-2 text-sm text-[#dfe8d8] hover:text-[#7AB547]"
        onclick={closeMobile}
      >
        <Phone class="size-4" />
        {SITE_CONTACT.phone}
      </a>

      <!-- Cart (mobile) -->
      {#if canUseCart}
        <button
          type="button"
          class="mb-3 flex w-full items-center gap-2 rounded-xl border border-[rgba(122,181,71,0.2)] p-3 text-left text-sm text-white"
          onclick={() => {
            closeMobile()
            openCart()
          }}
        >
          <ShoppingCart class="size-4 text-[#7AB547]" />
          Корзина {displayCart.count > 0 ? `(${displayCart.count})` : ''}
        </button>
      {/if}

      <!-- Categories -->
      <p class="mb-2 text-xs font-semibold uppercase tracking-wider text-[#dfe8d8]">Каталог</p>
      <ul class="mb-4 space-y-1">
        {#each navCategories as cat (cat.name)}
          {#if cat.slug}
            <li>
              <a
                href="/catalog#cat-{cat.slug}"
                class="block rounded-lg px-3 py-2 text-sm text-[#f4f8f1] transition-colors hover:bg-[#7AB547]/10 hover:text-white"
                onclick={closeMobile}
              >
                {cat.name}
              </a>
            </li>
          {/if}
        {/each}
        <li>
          <a
            href={siteCsaNavLink.href}
            class="block rounded-lg px-3 py-2 text-sm font-medium text-[#7AB547] transition-colors hover:bg-[#7AB547]/10"
            onclick={closeMobile}
          >
            {siteCsaNavLink.label}
          </a>
        </li>
      </ul>

      <!-- Info links -->
      <p class="mb-2 text-xs font-semibold uppercase tracking-wider text-[#dfe8d8]">
        Информация
      </p>
      <ul class="mb-4 space-y-1">
        {#each siteInfoNavLinks as link (link.href)}
          <li>
            <a
              href={link.href}
              class="block rounded-lg px-3 py-2 text-sm text-[#f4f8f1] transition-colors hover:bg-[#7AB547]/10 hover:text-white"
              onclick={closeMobile}
            >
              {link.label}
            </a>
          </li>
        {/each}
      </ul>

      <!-- Auth (mobile) -->
      {#if !siteCustomer}
        <div class="flex gap-2">
          <a
            href="/login"
            class="flex-1 rounded-xl border border-[rgba(122,181,71,0.3)] py-2.5 text-center text-sm font-medium text-white"
            onclick={closeMobile}
          >
            Войти
          </a>
          <a
            href="/register"
            class="flex-1 rounded-xl bg-[#7AB547] py-2.5 text-center text-sm font-semibold text-[#09100a]"
            onclick={closeMobile}
          >
            Регистрация
          </a>
        </div>
      {:else}
        <a
          href="/account"
          class="flex items-center gap-2 rounded-xl border border-[rgba(122,181,71,0.2)] p-3 text-sm text-white"
          onclick={closeMobile}
        >
          <User class="size-4 text-[#7AB547]" />
          {siteCustomer.fullName}
        </a>
      {/if}
    </div>
  {/if}
</header>
